import React from 'react';
import styled, { useTheme } from 'styled-components';
import Modal from '../../Modal/Modal';
import { H2, PSmall } from '../../elements/Text';
import { CloseIcon } from '../../Icons';
import { useGetAssessmentTests } from '../../../hooks/use-hydra/admin';
import { toast } from 'react-toastify';
import { inviteUsersToAssessmentTests } from '../../../api/admin';
import { useMutation } from 'react-query';
import useAuth from '../../../hooks/use-auth';
import { AxiosError } from 'axios';
import Papa from 'papaparse';
import staticTheme from '../../../theme';
import { createColumnHelper } from '@tanstack/react-table';

import Table from '../../Table/CompactTable';
import {
  CollapsibleSectionFooter,
  CollapsibleSectionFooterButton,
  CollapsibleSectionLabel,
} from '../CollapsibleSection';
import { StylesConfig } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { SmallPortalTooltip } from '../../Tooltip';
import { TooltipContent, TooltipDetail } from '../Progress/Tooltip';
const Content = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 32px;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
`;

type NewTestAssignment = {
  name: string;
  email: string;
  testId: number | null;
};

type TestSelectOption = {
  label: string;
  value: number;
};

const columnHelper = createColumnHelper<NewTestAssignment>();

const cleanString = (str: string) => {
  return str.replace(/\s+/g, ' ').replace(/\n|\r/g, '').trim();
};

const EditableDiv = ({
  value,
  onBlur,
  onPaste,
}: {
  value: string;
  onBlur: (e: React.FocusEvent<HTMLDivElement>) => void;
  onPaste: (e: React.ClipboardEvent<HTMLDivElement>) => void;
}) => (
  <EditableDivWrapper
    contentEditable
    onBlur={onBlur}
    onPaste={onPaste}
    suppressContentEditableWarning={true}
  >
    {value}
  </EditableDivWrapper>
);

const EditableDivWrapper = styled.div`
  width: 100%;
  overflow: hidden;

  &[contenteditable]:focus {
    outline: 0px solid transparent;
  }
`;

const IconContainer = styled.div`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 16px;
  padding: 4px 8px;
`;

const DeleteButton = styled(IconContainer)`
  color: ${({ theme }) => theme.colors.textDanger};

  &:hover {
    opacity: 0.8;
  }
`;

const HeaderRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
`;

const EditableCell = ({
  getValue,
  row: { index },
  column: { id },
  table,
}: {
  getValue: () => string;
  row: { index: number };
  column: { id: string };
  table: unknown;
}) => {
  const initialValue = getValue();
  const [value, setValue] = React.useState(initialValue);

  const onBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    const rawValue = e.target.innerText;
    const cleanedValue = cleanString(rawValue);

    // Update DOM directly since React can't control contentEditable
    if (rawValue !== cleanedValue) {
      e.target.innerText = cleanedValue;
    }

    // @ts-expect-error - table is not typed
    table?.options?.meta?.onUpdateData?.(index, id, cleanedValue);
  };

  const onPaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    const cleanedText = cleanString(pastedText);

    // Insert cleaned text at cursor position
    document.execCommand('insertText', false, cleanedText);
  };

  React.useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  return <EditableDiv value={value} onBlur={onBlur} onPaste={onPaste} />;
};

const InfoIcon = ({
  tooltip,
  rowId,
  isInvalid,
}: {
  tooltip: string;
  rowId: string;
  isInvalid: boolean;
}) => {
  const tooltipId = `info-tooltip-${rowId}`;
  const theme = useTheme();
  return (
    <React.Fragment>
      <IconContainer
        data-tooltip-id={tooltipId}
        style={{
          color: isInvalid ? theme.colors.secondary : theme.colors.primary,
        }}
      >
        <i className="fa-solid fa-info-circle" />
      </IconContainer>
      <SmallPortalTooltip anchorSelect={`[data-tooltip-id="${tooltipId}"]`}>
        <TooltipContent>
          <TooltipDetail>{tooltip}</TooltipDetail>
        </TooltipContent>
      </SmallPortalTooltip>
    </React.Fragment>
  );
};

function parseTests({
  testAssignments,
  newTestees,
}: {
  testAssignments: NewTestAssignment[];
  newTestees: NewTestAssignment[];
}): {
  validTestees: NewTestAssignment[];
  invalidTestees: NewTestAssignment[];
} {
  return newTestees.reduce<{
    validTestees: NewTestAssignment[];
    invalidTestees: NewTestAssignment[];
  }>(
    (acc, testee) => {
      if (isRowInvalid(testee, testAssignments)) {
        acc.invalidTestees.push(testee);
      } else {
        acc.validTestees.push(testee);
      }
      return acc;
    },
    { validTestees: [], invalidTestees: [] }
  );
}

const TableWrapper = styled.div`
  margin-top: 16px;
  max-height: 58vh;
  > div {
    border-radius: 8px;
    border: 1px solid ${({ theme }) => theme.colors.bgActive};
    max-height: 58vh;
    > div {
      border-radius: 8px;
    }
  }
  .new-tests-table {
    th {
      border-top: none !important;
    }
    th,
    td {
      &:last-child {
        border-right: none !important;
      }
      &:first-child {
        border-left: none !important;
      }
    }

    tbody > tr:last-child > td {
      border-bottom: none;
    }

    thead > tr > th {
      background-color: #212123 !important;
    }
  }
`;

const Wrapper = styled(Modal)`
  padding-bottom: 72px;
  padding-top: 64px;
  width: 80vw;
  min-width: 400px;
  overflow-y: auto;
  background-color: ${({ theme }) => theme.colors.bgPage2};
  border: 1px solid ${({ theme }) => theme.colors.bgActive};
`;

const CloseButton = styled(CloseIcon)`
  padding: 0;
`;

const Header = styled(H2)`
  margin: 0;
  padding: 0;
`;

function getDuplicateEmails(assignments: NewTestAssignment[]): Set<string> {
  const seen = new Set<string>();
  const duplicates = new Set<string>();
  for (const a of assignments) {
    if (!a.email) continue;
    const email = a.email.trim().toLowerCase();
    if (seen.has(email)) {
      duplicates.add(email);
    } else {
      seen.add(email);
    }
  }
  return duplicates;
}

function isRowInvalid(
  row: NewTestAssignment,
  assignments: NewTestAssignment[]
): boolean {
  if (!row.name || !row.email || !row.testId) return true;
  const email = row.email.trim().toLowerCase();
  const duplicates = getDuplicateEmails(assignments);
  if (duplicates.has(email)) {
    return true;
  }
  return false;
}

function getInfoTooltip(
  row: NewTestAssignment,
  assignments: NewTestAssignment[]
): string {
  if (!row.name || !row.email || !row.testId) {
    return 'Missing Data.';
  }
  const email = row.email.trim().toLowerCase();
  const duplicates = getDuplicateEmails(assignments);
  if (duplicates.has(email)) {
    return 'Duplicate email in the table.';
  }
  return 'Valid row.';
}

export default function InviteUsersToAssessmentTestModal({
  isOpen,
  onClose,
  companyId,
  cohortId,
}: {
  isOpen: boolean;
  onClose: () => void;
  companyId: number;
  cohortId: number;
}) {
  const {
    data: assessmentTests,
    refetch: refetchCompany,
    isLoading: isLoadingCompany,
  } = useGetAssessmentTests(companyId);

  const { getTokenSilently } = useAuth();
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const onFileUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      onFileUpload(text);
    };
    reader.readAsText(file);
  };

  const testOptions = React.useMemo(
    () =>
      assessmentTests?.tests.map((test) => ({
        label: test.name,
        value: test.assessmentTestId,
      })) ?? [],
    [assessmentTests]
  );

  const [newTestAssignments, setNewTestAssignments] = React.useState<
    NewTestAssignment[]
  >([
    {
      name: '',
      email: '',
      testId: null,
    },
  ]);
  const { validTestees, invalidTestees } = parseTests({
    testAssignments:
      (assessmentTests?.testAssignments ?? []).map((test) => ({
        name: test.name,
        email: test.user.email,
        testId: test.assessmentTestId,
      })) ?? [],
    newTestees: newTestAssignments,
  });

  const onAddRow = () => {
    setNewTestAssignments([
      ...newTestAssignments,
      { name: '', email: '', testId: null },
    ]);
  };

  const onDeleteRow = React.useCallback(
    (rowIndex: number) => {
      if (newTestAssignments.length === 1) {
        toast.error('Cannot delete the last test assignment');
        return;
      }
      setNewTestAssignments(
        newTestAssignments.filter((_, index) => index !== rowIndex)
      );
    },
    [newTestAssignments]
  );

  const onUpdateData = React.useCallback(
    (rowIndex: number, columnId: string, value: string | number | null) => {
      setNewTestAssignments(
        newTestAssignments.map((row, index) => {
          if (index === rowIndex) {
            return {
              ...row,
              [columnId]:
                typeof value === 'string' ? cleanString(value) : value,
            };
          }
          return row;
        })
      );
    },
    [newTestAssignments]
  );

  const onFileUpload = (text: string) => {
    const results = Papa.parse<NewTestAssignment>(text, {
      header: true,
      skipEmptyLines: true,
    });

    const rows: NewTestAssignment[] = results.data.map((row) => ({
      name: cleanString(row.name || ''),
      email: cleanString(row.email || ''),
      testId: null,
    }));

    const isTesteeValid = (testee: NewTestAssignment) => {
      return testee.name && testee.email;
    };

    const validTestees = rows.filter(isTesteeValid);
    const invalidTestees = rows.filter((testee) => !isTesteeValid(testee));

    if (validTestees.length === 0) {
      toast.error('Could not read any testees from the CSV');
      return;
    }

    toast.success(
      `Read ${validTestees.length} testees from the CSV.${
        invalidTestees.length > 0
          ? ` ${invalidTestees.length} invalid rows`
          : ''
      }`
    );

    setNewTestAssignments([...newTestAssignments, ...validTestees]);
  };

  const addTesteesMutation = useMutation({
    mutationFn: async () => {
      const token = await getTokenSilently();
      // Clean all testee data before sending (defense in depth)
      const response = await inviteUsersToAssessmentTests(token, {
        companyId,
        cohortId,
        testAssignments: validTestees.map((testee) => ({
          name: cleanString(testee.name),
          email: cleanString(testee.email),
          assessmentTestId: testee.testId ?? 0,
        })),
      });
      return response;
    },
    onSuccess: async () => {
      await refetchCompany();
      toast.success('Testees added successfully');
      setNewTestAssignments([]);
      onClose();
    },
    onError: (error: Error | AxiosError) => {
      toast.error(`Failed to add testees - ${error.message}`);
    },
  });

  const theme = useTheme();

  const onSaveTestees = () => {
    addTesteesMutation.mutate();
  };

  const columns = React.useMemo(
    () => [
      columnHelper.accessor('name', {
        header: 'Name',
        cell: EditableCell,
        meta: {
          styles: {
            minWidth: '20vw',
            maxWidth: '20vw',
          },
        },
      }),
      columnHelper.accessor('email', {
        header: 'Email',
        cell: EditableCell,
        meta: {
          styles: {
            minWidth: '20vw',
            maxWidth: '20vw',
          },
        },
      }),
      columnHelper.display({
        id: 'test',
        header: 'Test',
        cell: ({ row, table }) => {
          return (
            <React.Fragment>
              <CreatableSelect
                isMulti={false}
                options={testOptions}
                menuPortalTarget={document.body}
                placeholder="Select test..."
                isValidNewOption={() => false}
                value={testOptions.find(
                  (test) => test.value === row.original.testId
                )}
                onChange={(value) => {
                  // @ts-expect-error - table is not typed
                  table.options.meta?.onUpdateData?.(
                    row.index,
                    'testId',
                    value?.value ?? null
                  );
                }}
                styles={selectCustomStyles}
              />
            </React.Fragment>
          );
        },
        meta: {
          styles: {
            minWidth: '28vw',
            maxWidth: '28vw',
          },
        },
      }),
      columnHelper.display({
        id: 'actions',
        header: '',
        cell: ({ row, table }) => {
          // Add isInvalid and tooltip logic here
          const invalid = isRowInvalid(row.original, newTestAssignments);
          const tooltip = getInfoTooltip(row.original, newTestAssignments);
          return (
            <div style={{ display: 'flex', flexDirection: 'row', gap: 4 }}>
              <InfoIcon
                tooltip={tooltip}
                rowId={row.original.email}
                isInvalid={invalid}
              />
              <DeleteButton
                onClick={() => {
                  // @ts-expect-error - table is not typed
                  table.options.meta?.onDeleteRow?.(row.index);
                }}
                title="Delete row"
              >
                <i className="fa-solid fa-trash"></i>
              </DeleteButton>
            </div>
          );
        },
        meta: {
          styles: {
            minWidth: '6vw',
            maxWidth: '6vw',
            textAlign: 'center',
          },
        },
      }),
    ],
    [testOptions, newTestAssignments]
  );

  const cohortName = React.useMemo(() => {
    const cohort = assessmentTests?.cohorts.find(
      (cohort) => cohort.id === cohortId
    );
    return cohort?.name;
  }, [assessmentTests, cohortId]);

  React.useEffect(() => {
    if (!isOpen) {
      setNewTestAssignments([{ name: '', email: '', testId: null }]);
    }
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  return (
    <Wrapper>
      <HeaderRow>
        <Header>Invite Users to Assessment Test</Header>
        <CloseButton onClick={onClose} />
      </HeaderRow>
      <Content>
        <PSmall style={{ marginTop: 16 }}>
          Invite users to the assessment test for cohort <b>{cohortName}</b>.
        </PSmall>
        {newTestAssignments.length > 0 && (
          <TableWrapper>
            <Table
              className="new-tests-table"
              data={newTestAssignments}
              shouldPinHeaderRow
              columns={columns}
              onDeleteRow={onDeleteRow}
              onUpdateData={onUpdateData}
              footer={
                <CollapsibleSectionFooter>
                  <CollapsibleSectionLabel>
                    {newTestAssignments?.length} tests.{' '}
                    <span style={{ color: theme.colors.secondary }}>
                      {invalidTestees.length} invalid rows
                    </span>
                  </CollapsibleSectionLabel>
                  <Row>
                    <CollapsibleSectionFooterButton
                      label="Add testees"
                      onClick={onAddRow}
                      faIcon="fa-solid fa-plus"
                    />
                    <CollapsibleSectionFooterButton
                      label="Import CSV"
                      onClick={onFileUploadClick}
                      faIcon="fa-solid fa-file-import"
                    />
                    <input
                      type="file"
                      ref={fileInputRef}
                      style={{ display: 'none' }}
                      accept=".csv"
                      onChange={handleFileChange}
                    />
                    <CollapsibleSectionFooterButton
                      label="Invite testees"
                      onClick={onSaveTestees}
                      type="primary"
                      faIcon="fa-solid fa-save"
                      isDisabled={invalidTestees.length > 0}
                      tooltip="Invite all valid testees to the assessment test. No email invites are sent."
                      isLoading={
                        addTesteesMutation.isLoading || isLoadingCompany
                      }
                    />
                  </Row>
                </CollapsibleSectionFooter>
              }
            />
          </TableWrapper>
        )}
      </Content>
    </Wrapper>
  );
}

const selectCustomStyles: StylesConfig<TestSelectOption, false> = {
  menu: (provided) => ({
    ...provided,
    transition: 'all 0.3s ease-in-out',
    backgroundColor: staticTheme.colors.inputBackground,
    borderColor: staticTheme.colors.bgActive,
    borderStyle: 'solid',
    borderWidth: '1px',
    borderRadius: '8px',
  }),
  menuPortal: (provided) => ({
    ...provided,
    zIndex: 3,
  }),
  container: (provided) => ({
    ...provided,
    margin: 0,
    marginTop: 0,
    paddingBottom: 0,
    width: '100%',
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: 0,
    flexWrap: 'nowrap',
    overflow: 'auto',
    width: '100%',
    scrollbarWidth: 'none',
  }),
  dropdownIndicator: (provided) => ({ ...provided, display: 'none' }),
  clearIndicator: (provided) => ({ ...provided, display: 'none' }),
  indicatorsContainer: (provided) => ({
    ...provided,
    display: 'none',
  }),
  control: (provided) => ({
    ...provided,
    transition: 'all 0.3s ease-in-out',
    backgroundColor: 'transparent',
    borderColor: 'transparent',
    padding: 0,
    margin: 0,
    height: '100%',
    borderRadius: '8px',
    boxShadow: '0 !important',
    outline: 'none',
    '&:hover': {
      borderColor: 'transparent',
    },
    cursor: 'pointer',
  }),
  option: (
    provided,
    { isSelected, isFocused }: { isSelected: boolean; isFocused: boolean }
  ) => ({
    ...provided,
    backgroundColor:
      isSelected || isFocused
        ? staticTheme.colors.fieldBackground
        : 'transparent',
    color:
      isSelected || isFocused
        ? staticTheme.colors.textPrimary
        : staticTheme.colors.textRegular,
    cursor: isSelected ? 'not-allowed' : 'default',
    '&:active': {
      color: staticTheme.colors.textPrimary,
      backgroundColor: staticTheme.colors.fieldBackground,
    },
  }),
  singleValue: (provided) => ({
    ...provided,
    color: staticTheme.colors.inputTextActive,
  }),
  input: (provided) => ({
    ...provided,
    color: staticTheme.colors.inputTextActive,
  }),
  placeholder: (provided) => ({
    ...provided,
    color: staticTheme.colors.inputTextPlaceholder,
  }),
};
